import {
  createUserWithEmailAndPassword,
  deleteUser,
  signOut
} from 'firebase/auth';
import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import type { OrgRole } from '@finvox/shared/org';
import type { MemberPermissionOverrides } from '@finvox/shared/member-access';
import { getFirestoreDb } from '@/lib/firebase';
import { getProvisionerAuth, getProvisionerFirestore } from './provisioner';
import { normalizeMemberPermissionPayload } from './permissions';
import { assertAssignableRole, assertValidProvisionInput } from './validation';
import { mapProvisionError } from './errors';

export type ProvisionedUserDoc = {
  email: string;
  defaultOrgId: string;
  memberships: Array<{ orgId: string; orgName: string; role: OrgRole }>;
  createdAt: ReturnType<typeof serverTimestamp>;
  updatedAt: ReturnType<typeof serverTimestamp>;
};

export function buildProvisionedUserDoc(
  orgId: string,
  orgName: string,
  email: string,
  role: OrgRole
): ProvisionedUserDoc {
  return {
    email,
    defaultOrgId: orgId,
    memberships: [{ orgId, orgName, role }],
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  };
}

export async function provisionOrgMember(
  orgId: string,
  ownerUid: string,
  email: string,
  password: string,
  role: OrgRole,
  permissions?: MemberPermissionOverrides | null
): Promise<{ ok: true; uid: string }> {
  const normalized = assertValidProvisionInput(email, password);
  assertAssignableRole(role);

  const orgSnap = await getDoc(doc(getFirestoreDb(), 'organizations', orgId));
  if (!orgSnap.exists()) throw new Error('Organization not found');
  const orgName = String(orgSnap.data()?.name ?? orgId);

  const provisionerAuth = getProvisionerAuth();
  const provisionerDb = getProvisionerFirestore();
  let targetUid: string | undefined;

  try {
    const cred = await createUserWithEmailAndPassword(provisionerAuth, normalized, password);
    targetUid = cred.user.uid;

    await setDoc(
      doc(provisionerDb, 'users', targetUid),
      buildProvisionedUserDoc(orgId, orgName, normalized, role)
    );

    const memberData: Record<string, unknown> = {
      role,
      email: normalized,
      uid: targetUid,
      addedBy: ownerUid,
      joinedAt: serverTimestamp()
    };
    const normalizedPerms = normalizeMemberPermissionPayload(permissions);
    if (normalizedPerms) memberData.permissions = normalizedPerms;

    await setDoc(doc(getFirestoreDb(), 'organizations', orgId, 'members', targetUid), memberData);

    return { ok: true, uid: targetUid };
  } catch (e) {
    if (targetUid && provisionerAuth.currentUser?.uid === targetUid) {
      try {
        await deleteUser(provisionerAuth.currentUser);
      } catch {
        /* best-effort rollback */
      }
    }
    throw mapProvisionError(e);
  } finally {
    try {
      await signOut(provisionerAuth);
    } catch {
      /* ignore */
    }
  }
}
